import { FIRMA_ROLLE_OPTIONS } from "@kmvs/forms-core";
import { UNTERNEHMENSFUNKTIONEN } from "../../data/unternehmensfunktion";
import type { Answers, FirmaEntry, Kontaktdaten, PersonEntry, VerantEntry } from "./types";

/**
 * Welche Firmen aus der Analyse als Kontakt angelegt werden können.
 *
 * Drei Listen, ein Vorgang: die externen Dienstleister aus den
 * Verantwortlichkeiten (schon länger übernehmbar) sowie die Unternehmen aus
 * Verbund und Beteiligungen. Für die beiden letzten gab es bis hierher keinen
 * Weg in den Kontakt — der Verbund, den der Berater eine Stunde zuvor
 * aufgenommen hatte, blieb im Antwortsatz stehen und tauchte im Organigramm
 * nie auf.
 *
 * Die Übernahme bleibt eine Entscheidung des Beraters, kein Nebeneffekt des
 * Bestätigens: Ein automatisch angelegter Firmen-Kontakt ist schwerer wieder
 * loszuwerden als einer, den jemand gewollt hat.
 */

export type UebernahmeQuelle =
  | "verantwortlichkeiten"
  | "verbund_firmen"
  | "beteiligungen_firmen"
  | "geschaeftsfuehrer"
  | "gesellschafter";

/**
 * In welches Feld des Eintrags die neue Kontakt-Nummer geschrieben wird.
 *
 * Eine externe Zuständigkeit trägt beides: die Kanzlei UND den Sachbearbeiter
 * darin. Deshalb reicht ein Feld nicht — sonst überschriebe die Person die
 * Firma, und die Beziehung verlöre die eine oder die andere Seite.
 */
export type UebernahmeZielFeld = "uebernommenAlsContactId" | "personContactId";

export type UebernahmeKandidat = {
  /** Id des Eintrags in seiner Liste. */
  id: string;
  quelle: UebernahmeQuelle;
  art: "firma" | "person";
  zielFeld: UebernahmeZielFeld;
  /** Anzeigename in der Liste. */
  bezeichnung: string;
  /** Zeile unter dem Namen — Funktion, Branche, Verbindung, Kontaktwege. */
  merkmale: string[];
  /** Felder für `buildCompanyContactCreatePayload` bzw. den Personen-Bauer. */
  felder: Record<string, string>;
  uebernommenAlsContactId?: string;
};

/**
 * Eindeutiger Schlüssel eines Kandidaten.
 *
 * Die Eintrags-Id allein reicht nicht: Eine externe Zuständigkeit erscheint
 * zweimal in derselben Liste — einmal als Kanzlei, einmal als Sachbearbeiter.
 * Beide tragen dieselbe Id und würden sonst denselben Knopf bedienen.
 */
export const kandidatSchluessel = (k: UebernahmeKandidat): string =>
  `${k.quelle}:${k.zielFeld}:${k.id}`;

export const QUELLE_TITEL: Record<UebernahmeQuelle, string> = {
  verantwortlichkeiten: "Zuständigkeit",
  verbund_firmen: "Unternehmensverbund",
  beteiligungen_firmen: "Beteiligung",
  geschaeftsfuehrer: "Geschäftsführung",
  gesellschafter: "Gesellschafter",
};

/**
 * Rechtsform-Rückfall.
 *
 * `buildCompanyContactCreatePayload` gibt ohne Rechtsform `null` zurück. Die
 * Übernahme soll nicht daran scheitern, dass sie im Fragebogen nicht angegeben
 * wurde — korrigierbar ist sie danach in der Akte, verloren wäre die Übernahme
 * sonst ganz.
 */
const RECHTSFORM_RUECKFALL = "Sonstige";

/**
 * Anschrift aus einer Zeile.
 *
 * Die Analyse erfasst „Firmierung, Anschrift" als EIN Feld (Excel Nr. 251/255).
 * Der Kontakt führt Straße, PLZ und Ort getrennt. Zerlegt wird nur die klar
 * erkennbare deutsche Form „Straße Hausnummer, PLZ Ort"; alles andere bleibt
 * ungeteilt und geht als Bemerkung mit. Lieber eine Bemerkung, die jemand
 * liest, als eine falsch geratene Adresse, die niemand mehr prüft.
 */
export function anschriftZerlegen(zeile: string | undefined): {
  strasse?: string;
  plz?: string;
  ort?: string;
} {
  const text = (zeile ?? "").trim();
  if (!text) return {};
  const treffer = /^(.*?)[,;]?\s*(\d{5})\s+(.+)$/.exec(text);
  if (!treffer) return {};
  const strasse = treffer[1].trim().replace(/[,;]$/, "");
  const ort = treffer[3].trim();
  if (!ort) return {};
  return { ...(strasse ? { strasse } : {}), plz: treffer[2], ort };
}

/**
 * Rollen der Analyse → Unternehmensfunktion des Kontakts.
 *
 * Übernommen wird nur, was der Kontakt auch kennt: „Verwaltungsgesellschaft"
 * ist dort seit dem 27.08.2026 gestrichen und nicht mehr anwählbar. Geprüft
 * wird gegen die Liste selbst statt gegen eine abgeschriebene Ausnahme — fällt
 * dort etwas weg, wandert es hier automatisch mit.
 */
export function unternehmensfunktionAusRollen(rollen: string[] | undefined): string {
  if (!Array.isArray(rollen)) return "";
  const erlaubt = new Set<string>(UNTERNEHMENSFUNKTIONEN);
  return rollen
    .map((code) => FIRMA_ROLLE_OPTIONS.find((o) => o.value === code)?.label)
    .filter((label): label is string => !!label && erlaubt.has(label))
    .join("; ");
}

const nichtLeer = (...werte: Array<string | undefined>): string[] =>
  werte.map((w) => (w ?? "").trim()).filter(Boolean);

/** Die Kontaktwege einer Person → Felder des Personen-Payloads. */
const personenWege = (kontakt: Kontaktdaten | undefined): Record<string, string> => {
  const k = kontakt ?? {};
  const anschrift = anschriftZerlegen(k.adresse);
  return {
    ...(k.telefonPrivat ? { phonePrivate: k.telefonPrivat } : {}),
    ...(k.telefonGeschaeftlich ? { phoneWork: k.telefonGeschaeftlich } : {}),
    ...(k.mobilPrivat ? { mobilePrivate: k.mobilPrivat } : {}),
    ...(k.mobilGeschaeftlich ? { mobileWork: k.mobilGeschaeftlich } : {}),
    ...(k.emailPrivat ? { emailPrivate: k.emailPrivat } : {}),
    ...(k.emailGeschaeftlich ? { emailWork: k.emailGeschaeftlich } : {}),
    ...(anschrift.strasse ? { street: anschrift.strasse } : {}),
    ...(anschrift.plz ? { plz: anschrift.plz } : {}),
    ...(anschrift.ort ? { ort: anschrift.ort } : {}),
  };
};

const personenName = (vorname?: string, nachname?: string): string =>
  nichtLeer(vorname, nachname).join(" ");

/** Geschäftsführer und Gesellschafter — je ein Personen-Kontakt in spe. */
const ausPerson = (eintrag: PersonEntry, quelle: UebernahmeQuelle): UebernahmeKandidat => ({
  id: eintrag.id,
  quelle,
  art: "person",
  zielFeld: "uebernommenAlsContactId",
  bezeichnung: personenName(eintrag.vorname, eintrag.nachname),
  merkmale: nichtLeer(
    eintrag.funktion,
    eintrag.anteile ? `${eintrag.anteile} %` : undefined,
    eintrag.geburtsdatum,
  ),
  felder: {
    lastname: (eintrag.nachname ?? "").trim(),
    ...((eintrag.vorname ?? "").trim() ? { firstname: (eintrag.vorname ?? "").trim() } : {}),
    ...((eintrag.geburtsdatum ?? "").trim()
      ? { dateofbirth: (eintrag.geburtsdatum ?? "").trim() }
      : {}),
    ...((eintrag.funktion ?? "").trim() ? { position: (eintrag.funktion ?? "").trim() } : {}),
    ...personenWege(eintrag.kontakt),
  },
  uebernommenAlsContactId: eintrag.uebernommenAlsContactId,
});

/**
 * Die PERSON hinter einer Zuständigkeit — intern wie extern.
 *
 * Bei einer externen Zuständigkeit steht sie neben der Kanzlei, nicht statt
 * ihrer: Die Beziehung zum Unternehmen hängt am Menschen, die Kanzlei liegt
 * als „arbeitet bei" daneben.
 */
const ausVerantPerson = (eintrag: VerantEntry): UebernahmeKandidat => ({
  id: eintrag.id,
  quelle: "verantwortlichkeiten",
  art: "person",
  zielFeld: "personContactId",
  bezeichnung: personenName(eintrag.vorname, eintrag.nachname),
  merkmale: nichtLeer(
    eintrag.funktion,
    eintrag.typ === "extern" ? eintrag.firmenname : undefined,
  ),
  felder: {
    lastname: (eintrag.nachname ?? "").trim(),
    ...((eintrag.vorname ?? "").trim() ? { firstname: (eintrag.vorname ?? "").trim() } : {}),
    ...((eintrag.funktion ?? "").trim() ? { position: (eintrag.funktion ?? "").trim() } : {}),
    // Der Arbeitgeber steht nur bei externen Zuständigen — ein interner
    // arbeitet beim analysierten Unternehmen und braucht keinen Eintrag.
    ...(eintrag.typ === "extern" && (eintrag.firmenname ?? "").trim()
      ? { employer: (eintrag.firmenname ?? "").trim() }
      : {}),
    ...personenWege(eintrag.kontakt),
  },
  uebernommenAlsContactId: eintrag.personContactId,
});

/** Die FIRMA hinter einer externen Zuständigkeit — die Kanzlei, das Büro. */
const ausVerantFirma = (eintrag: VerantEntry): UebernahmeKandidat => ({
  id: eintrag.id,
  quelle: "verantwortlichkeiten",
  art: "firma",
  zielFeld: "uebernommenAlsContactId",
  bezeichnung: (eintrag.firmenname ?? "").trim(),
  merkmale: nichtLeer(eintrag.funktion, eintrag.branche, eintrag.email, eintrag.telefon),
  felder: {
    name: (eintrag.firmenname ?? "").trim(),
    gesellschaftsform: (eintrag.gesellschaftsform ?? "").trim() || RECHTSFORM_RUECKFALL,
    phone: eintrag.telefon ?? "",
    email: eintrag.email ?? "",
    website: eintrag.web ?? "",
  },
  uebernommenAlsContactId: eintrag.uebernommenAlsContactId,
});

const ausFirma = (eintrag: FirmaEntry, quelle: UebernahmeQuelle): UebernahmeKandidat => {
  const anschrift = anschriftZerlegen(eintrag.anschrift);
  const funktion = unternehmensfunktionAusRollen(eintrag.rollen);
  return {
    id: eintrag.id,
    quelle,
    art: "firma",
    zielFeld: "uebernommenAlsContactId",
    bezeichnung: (eintrag.firmenname ?? "").trim(),
    merkmale: nichtLeer(funktion, eintrag.anschrift, eintrag.taetigkeit),
    felder: {
      name: (eintrag.firmenname ?? "").trim(),
      gesellschaftsform:
        (eintrag.gesellschaftsform ?? "").trim() || RECHTSFORM_RUECKFALL,
      ...(anschrift.strasse ? { strasse: anschrift.strasse } : {}),
      ...(anschrift.plz ? { plz: anschrift.plz } : {}),
      ...(anschrift.ort ? { ort: anschrift.ort } : {}),
      // Ließ sich die Anschrift nicht zerlegen, geht sie ungeteilt mit, statt
      // verloren zu gehen.
      ...(!anschrift.plz && (eintrag.anschrift ?? "").trim()
        ? { remark: (eintrag.anschrift ?? "").trim() }
        : {}),
      ...(funktion ? { unternehmensfunktion: funktion } : {}),
      ...((eintrag.taetigkeit ?? "").trim()
        ? { taetigkeitsbeschreibung: (eintrag.taetigkeit ?? "").trim() }
        : {}),
    },
    uebernommenAlsContactId: eintrag.uebernommenAlsContactId,
  };
};

const liste = <T,>(wert: unknown): T[] => (Array.isArray(wert) ? (wert as T[]) : []);

/**
 * Alle übernehmbaren Firmen eines Antwortsatzes, in der Reihenfolge des
 * Fragebogens: erst der Verbund, dann die Beteiligungen, zuletzt die externen
 * Zuständigkeiten.
 *
 * Ein Eintrag ohne Firmennamen taucht nicht auf — er ließe sich nicht anlegen,
 * und ein Knopf, der nur eine Fehlermeldung erzeugt, ist kein Angebot.
 */
export function kandidatenAusAntworten(answers: Answers): UebernahmeKandidat[] {
  const zustaendige = liste<VerantEntry>(answers.verantwortlichkeiten).filter(
    (e) => e && typeof e === "object",
  );

  const kandidaten: UebernahmeKandidat[] = [
    ...liste<FirmaEntry>(answers.verbund_firmen).map((e) => ausFirma(e, "verbund_firmen")),
    ...liste<FirmaEntry>(answers.beteiligungen_firmen).map((e) =>
      ausFirma(e, "beteiligungen_firmen"),
    ),
    ...liste<PersonEntry>(answers.geschaeftsfuehrer).map((e) =>
      ausPerson(e, "geschaeftsfuehrer"),
    ),
    ...liste<PersonEntry>(answers.gesellschafter).map((e) => ausPerson(e, "gesellschafter")),
    // Ein externer Eintrag liefert bis zu ZWEI Kandidaten: die Kanzlei und den
    // Sachbearbeiter darin.
    ...zustaendige.filter((e) => e.typ === "extern").map(ausVerantFirma),
    ...zustaendige.map(ausVerantPerson),
  ];

  return kandidaten.filter(
    (k) =>
      !!k.id &&
      !!k.bezeichnung &&
      // Der Personen-Payload braucht den Nachnamen; ohne ihn liefert der Bauer
      // `null` und der Knopf erzeugte nur eine Fehlermeldung.
      (k.art === "firma" || !!k.felder.lastname),
  );
}
